// PWA service worker registration + offline evidence upload queue
// queueOfflineUpload: called when navigator.onLine === false during evidence upload
// flushOfflineQueue: called on 'online' event — processes queued uploads in order

// Type-only in the other direction, so this is not the cycle the inline api import avoids.
import { evidenceParentFields } from './evidence-target.js';

const DB_NAME = 'buildc-offline';
const STORE_NAME = 'evidence-queue';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * What a queued photograph belongs to.
 *
 * A discriminated union rather than a bare id because the queue is about to carry more than
 * one kind of upload, and the two differ in where they are recorded — not in how they are
 * held. The site visit member landed with the site visit ticket, and adding it is what forced
 * every call site open — which is why the switches below are written out rather than reading
 * target.phaseId inline.
 *
 * A site visit carries no round grant. Rounds belong to a phase the client sends back; nobody
 * sends a survey back, and the visit is not sealed on completion the way evidence is by
 * INV-016.
 */
export type UploadTarget = {
  kind: 'site_visit';
  siteVisitId: string;
} | EvidenceRecordTarget;

/**
 * The records an `evidence` row can be filed against (migration 030). Only a phase carries a
 * round grant: nothing else has submission rounds. A change order and a dispute are sealed by
 * the API once decided or settled, so a photograph queued before that lands in the tray with
 * the INV-016 reason, exactly as a phase photograph does.
 */
export type EvidenceRecordTarget = {
  kind: 'phase';
  phaseId: string;
  /**
   * The signed grant naming the submission round that was open when the photograph was
   * taken, carried from the phase the builder was looking at. Sent back with the upload so a
   * photograph held on the phone overnight is still filed under the submission it belongs to
   * rather than the one the client's complaint opened. Absent on items queued by an older
   * install, which the API records under the current round as it always did.
   */
  roundToken?: string;
} | {
  kind: 'variation';
  variationId: string;
} | {
  kind: 'compliance_item';
  complianceItemId: string;
} | {
  kind: 'dispute';
  disputeId: string;
} | {
  // A defect on the snag list (BC-186). Sealed once the snag is decided, like a dispute.
  kind: 'snag';
  snagId: string;
} | {
  // An issue or RFI on the register (045). Builder-only files, sealed once the issue is settled.
  kind: 'issue';
  issueId: string;
} | {
  // A defect on a completed project (BC-201). The API decides which group a photo is in, the
  // report or the fix, and seals them when the defect closes.
  kind: 'defect';
  defectId: string;
} | {
  kind: 'project';
  projectId: string;
};

export interface QueuedUpload {
  target: UploadTarget;
  file: Blob;
  filename: string;
  mimeType: string;
  caption?: string;
  gpsLat?: number;
  gpsLng?: number;
  takenAt?: string;
  queuedAt: string;
  /**
   * Set when the API refused this photo for a reason retrying cannot fix — the phase was
   * approved while the device was offline. The item stays in the store so the builder can
   * see what did not make it, but the flush skips it from then on. Deleting it instead
   * would lose a photograph they took on site without ever telling them.
   */
  rejectedReason?: string;
  rejectedAt?: string;
}

/**
 * What actually comes back out of the store, which is not the same thing as what we write.
 *
 * An install that queued photographs before targets existed wrote `phaseId` — and its round
 * grant — at the top level, and those items are still sitting on the device. They are read
 * as phase uploads rather than skipped: a photograph stranded on a phone is the failure this
 * queue exists to prevent, and nothing about the upload such an item describes has changed.
 */
export type StoredUpload = Omit<QueuedUpload, 'target'> & {
  id: number;
  target?: UploadTarget;
  phaseId?: string;
  roundToken?: string;
};

/**
 * Null only when the item names nothing at all — neither a target nor a legacy phase id.
 * The flush leaves such an item in the store rather than dropping it; there is nowhere to
 * send it, and a silent delete is the one outcome this queue never chooses.
 */
export function resolveUploadTarget(item: StoredUpload): UploadTarget | null {
  if (item.target) return item.target;
  if (item.phaseId) return { kind: 'phase', phaseId: item.phaseId, roundToken: item.roundToken };
  return null;
}

/**
 * Refusals no amount of retrying will clear. A sealed phase never re-opens for the photo
 * that missed it, so a retry loop would run for as long as the app is installed.
 */
const PERMANENT_REJECTION_CODES = [
  // The phase was approved while the device was offline; it never re-opens.
  'INV-016',
  // The round grant this photograph carries is one the server cannot recognise — a rotated
  // signing key, or a corrupted store. It will not start verifying on a later attempt, and
  // a photograph that silently never arrives is worse than one the builder is told about.
  'EVIDENCE_ROUND_TOKEN',
  'INV-024',
];

/**
 * Reads the INV-xxx code off a rejected request. Deliberately checks the field rather than
 * `instanceof ApiError`: this runs behind a dynamic import of api.js, and the only thing
 * that matters is whether the API named a business rule.
 */
function permanentRefusal(err: unknown): err is { code: string; message: string } {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' && PERMANENT_REJECTION_CODES.includes(code);
}

export async function queueEvidenceUpload(upload: QueuedUpload): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).add(upload);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getQueuedUploads(): Promise<StoredUpload[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** The photos the API refused outright — what the upload tray shows the builder. */
export async function getRejectedUploads(): Promise<StoredUpload[]> {
  const queued = await getQueuedUploads();
  return queued.filter((item) => item.rejectedReason != null);
}

export async function markQueuedUploadRejected(id: number, reason: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const read = store.get(id);
    read.onsuccess = () => {
      const record = read.result as StoredUpload | undefined;
      if (record) {
        store.put({ ...record, rejectedReason: reason, rejectedAt: new Date().toISOString() });
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function removeQueuedUpload(id: number): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// The worker is cache-first for static assets, and that is only safe because a
// production build gives every chunk a content-hashed *filename*. A dev server does
// not: `/src/pages/builder/Projects.tsx` is a stable path, and a pre-bundled dep
// carries its hash in a query string (`react-router-dom.js?v=5541f2ae`). A new `?v=`
// misses the cache and is fetched, but the previous generation stays cached and keeps
// being served — so one page ends up holding two optimizer generations, two React
// instances, and a null dispatcher. It surfaces as
// "Cannot read properties of null (reading 'useContext')" on the first hook of
// whichever route happens to load a stale chunk, and it survives a hard reload
// because the worker sits in front of the HTTP cache.
//
// So: register in production only, and in dev actively tear down a worker that was
// installed before this guard existed. Without the teardown the guard fixes nothing
// for anyone who has already run the app locally.
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  if (!import.meta.env.PROD) {
    void navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .then(() => caches?.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))))
      .catch(() => {});
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // SW registration is best-effort
    });
  });
}

// Convenience wrapper for UploadEvidenceModal — accepts a File directly
export async function queueOfflineUpload(opts: {
  target: UploadTarget;
  file: File;
  caption?: string;
  gps?: { lat: number; lng: number };
}): Promise<void> {
  await queueEvidenceUpload({
    target: opts.target,
    file: opts.file,
    filename: opts.file.name,
    mimeType: opts.file.type,
    caption: opts.caption,
    gpsLat: opts.gps?.lat,
    gpsLng: opts.gps?.lng,
    takenAt: new Date().toISOString(),
    queuedAt: new Date().toISOString(),
  });
}

let flushInFlight: Promise<void> | null = null;

// Flush queue when back online.
// Concurrent callers share one run: 'online' can fire more than once on a flaky
// site connection, and the upload modal can trigger a flush at the same time.
// Two runs would each read the same queued rows and upload every photo twice,
// double-counting the evidence trail.
export function flushOfflineQueue(): Promise<void> {
  flushInFlight ??= runFlush().finally(() => {
    flushInFlight = null;
  });
  return flushInFlight;
}

// import api inline to avoid circular deps
async function runFlush(): Promise<void> {
  const items = await getQueuedUploads();
  if (items.length === 0) return;

  const { api } = await import('./api.js');

  // Keyed on the target rather than written inline, so a second kind of upload cannot be
  // added without the compiler pointing at both places it has to be handled.
  const presignFor = (target: UploadTarget, item: StoredUpload) => {
    switch (target.kind) {
      case 'site_visit':
        return api.siteVisits.attachments.presign(target.siteVisitId, {
          filename: item.filename,
          mime_type: item.mimeType,
          file_size: item.file.size,
        });
      default:
        return api.evidence.presign({
          ...evidenceParentFields(target),
          filename: item.filename,
          mime_type: item.mimeType,
          file_size: item.file.size,
        });
    }
  };

  const recordFor = (target: UploadTarget, item: StoredUpload, s3Key: string) => {
    switch (target.kind) {
      case 'phase':
      case 'variation':
      case 'compliance_item':
      case 'dispute':
      case 'snag':
      case 'issue':
      case 'defect':
      case 'project':
        return api.evidence.record({
          ...evidenceParentFields(target),
          s3_key: s3Key,
          filename: item.filename,
          mime_type: item.mimeType,
          file_size: item.file.size,
          caption: item.caption,
          gps_lat: item.gpsLat,
          gps_lng: item.gpsLng,
          taken_at: item.takenAt,
          // Capture-time, not flush-time: the phase may have moved to a new round while this
          // photograph sat in the queue. Only a phase has rounds, so only a phase carries one.
          round_token: target.kind === 'phase' ? target.roundToken : undefined,
        });
      case 'site_visit':
        return api.siteVisits.attachments.record(target.siteVisitId, {
          s3_key: s3Key,
          filename: item.filename,
          mime_type: item.mimeType,
          file_size: item.file.size,
          caption: item.caption,
          gps_lat: item.gpsLat,
          gps_lng: item.gpsLng,
          taken_at: item.takenAt,
        });
    }
  };

  for (const item of items) {
    // Already refused. The tray owns it now; retrying would fail identically forever.
    if (item.rejectedReason != null) continue;

    const target = resolveUploadTarget(item);
    if (!target) continue;

    try {
      const { upload_url, s3_key } = await presignFor(target, item);

      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': item.mimeType },
        body: item.file,
      });
      if (!putRes.ok) continue;

      await recordFor(target, item, s3_key);

      await removeQueuedUpload(item.id);
    } catch (err) {
      // A refusal on a business rule is final: the phase was approved while this photo sat
      // in the queue, and the evidence record is sealed. Mark it so the builder is told,
      // rather than retrying it on every 'online' event for the life of the install.
      if (permanentRefusal(err)) {
        await markQueuedUploadRejected(item.id, err.message);
        continue;
      }
      // Anything else is a blip — leave the item queued and retry next time online.
    }
  }
}

// Register the online flush listener once at app startup
export function registerOfflineSync(): void {
  window.addEventListener('online', () => {
    flushOfflineQueue().catch(() => {});
  });
}
